<template>
  <VDialogV3
    ref="dialog"
    id="order-position-dialog"
    position="right"
    no-padding
    not-hide-on-click
    :tabs="tabs"
    :entity="localPosition"
    :feed-entity-pointer="positionPointer"
    :loading="localPosition === null"

    v-model:active-tab="activeTab"

    show-edit-button
    @edit-button:clicked="showRequisitesDialog"
  >
    <OrderPositionMainSection
      v-if="activeTab?.id === 'main'"
      :position="localPosition"
    />

    <OfferCharacteristicsSection
      v-if="activeTab?.id === 'characteristic'"
      :id="localPosition.offer.id"
    />

    <FilesList
      v-if="activeTab?.id === 'files' && isFilesAvailable"
      :entities="filesPointers"
      @change="$emit('renew-data')"
    />
  </VDialogV3>
</template>

<style lang="scss" src="./OrderPositionCard.scss" />

<script lang="ts">
import FilesService                from '@/core/services/Files/FilesService';
import EntityPointer               from '@/domains/EntityPointer/EntityPointer';
import OrderPosition               from '@/domains/Orders/OrderPosition';
import PreorderPosition            from '@/domains/Preorders/PreorderPosition';
import OrderPositionMainSection    from "@/domains/Orders/components/OrderPositionCard/OrderPositionMainSection.vue";
import OfferCharacteristicsSection from "@/domains/Offers/components/OfferCard/OfferCharacteristicsSection.vue";
import OrdersService               from "@/domains/Orders/OrdersService";

export default defineComponent({
  name: 'OrderPositionCard',

  components: {
    OrderPositionMainSection,
    OfferCharacteristicsSection
  },

  props: {
    /** ID позиции — диалог сам загрузит объект */
    id: {
      type: Number,
      default: null,
    },

    /** Нужен только при загрузке по id — бэк требует флаг */
    isPreorder: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localPosition: null as OrderPosition | PreorderPosition | null,

      activeTab: null as { id: string, name: string } | null,
    };
  },

  async mounted() {
    await this.init();
  },

  computed: {
    /** Тип сущности позиции: позиция предзаказа либо позиция заказа. */
    positionTypeName(): string {
      if (this.isPreorder) {
        return 'preorderPosition';
      } else {
        return 'orderPosition';
      }
    },

    /** Указатель на позицию для фида. Фид доступен только покупателю. */
    positionPointer(): EntityPointer | null {
      if (this.$core.currentPartner.isBuyer) {
        return EntityPointer.make(this.positionTypeName, this.id);
      } else {
        return null;
      }
    },

    /** Файлы доступны только покупателю. Правило одно на вкладку, компонент и указатели. */
    isFilesAvailable(): boolean {
      return this.$core.currentPartner.isBuyer;
    },

    /** Указатели на сущности, к которым цепляются файлы. Пустые отсеиваются. */
    filesPointers(): EntityPointer[] {
      if (this.isFilesAvailable) {
        return EntityPointer.collect([
          EntityPointer.make(this.positionTypeName, this.id, this.localPosition?.toString()),
          EntityPointer.make('order', this.localPosition?.order?.id, 'Заказ'),
          EntityPointer.make('demandRequest', this.localPosition?.demandRequest?.id, 'Потребность'),
          EntityPointer.make('demandOption', this.localPosition?.demandOption?.id, 'Вариант'),
        ]);
      } else {
        return [];
      }
    },

    isDragging(): boolean {
      return FilesService.isDragging;
    },

    tabs() {
      const tabs = [
        {id: 'main', name: 'Основное'},
      ];

      if (this.localPosition?.isOfferBased) {
        tabs.push({id: 'characteristic', name: 'Характеристики'});
      }

      if (this.isFilesAvailable) {
        tabs.push({id: 'files', name: 'Файлы'});
      }

      return tabs;
    },
  },

  methods: {
    async init(): Promise<void> {
      if (this.$core.utils.isNullOrUndefined(this.id) !== true) {
        this.localPosition = await OrdersService.getPosition(this.id, this.isPreorder, this.$core.currentUser.isAdmin);
      }
    },

    async showRequisitesDialog() {
      await this.$core.dialogs.OrderPositionRequisites.showAsPromise({
        position: this.localPosition,
      }, {
        save: async ({ position }) => {
          const isOk = await OrdersService.updatePosition(position)
          if (isOk) {
            this.$core.dialogs.OrderPositionRequisites.resolve();
            this.init();
            this.$refs.dialog.renewFeed();
          }
        }
      })
    },
  },
});
</script>